// Wet paint body (gouache, acrylic, oil): flow, drying and curing into film.
//
// Flow model: a thin Bingham-plastic film. Artists' paint has a yield stress:
// below it the paste holds its shape (that is why brush ridges and knife edges
// survive), above it it flows like a viscous liquid. For a film of thickness V
// on a surface slope |grad eta| the basal shear stress is ~ rho g V |grad eta|,
// so flow starts once |grad eta| > yield / V and the flux is
//     q = mobility * V^3 * (|grad eta| - yield / V)   (direction: downslope)
// Thick blobs slump, thin films hold, ridges relax only down to the yield slope.
// Mobility falls with openness squared as the paint stiffens while drying.
//
// Drying: openness decays at the cell's medium drying rate. When it reaches
// zero the body cures: its pigment is composited (Kubelka-Munk layer) onto the
// dried spectral stack, its solids become permanent relief, and any
// watercolour pigment underneath is locked beneath it first.

import { PIGMENT_SLOTS } from '../core/PaintState.js';
import { BAND_COUNT } from '../pigments/ColourConversion.js';
import { kmLayerReflectance } from '../pigments/SpectralMixing.js';
import { tileBounds, getScratch } from './region.js';
import { clearFaces, limitOutflow, carryAlongFaces, applyFaces } from './FluxTransport.js';

export const BODY_PRESENT = 1e-4;
// The GPU dispatches this many body-flow substeps per step (unused ones exit
// early); realistic paints need 1-2.
export const MAX_BODY_SUBSTEPS = 8;

export const DEFAULT_BODY_PARAMS = Object.freeze({
  reliefScale: 0.05, // paper relief seen by the paint body
  tiltCoupling: 0.15, // fraction of canvas tilt felt by the paint body
  maxSubstep: 0.1,
});

export function stepBodyFlow(state, region, dt, env = {}) {
  const p = { ...DEFAULT_BODY_PARAMS, ...(env.body || {}) };
  const { width, height, bodyVol, bodyOpen, bodyMob, bodyYield, dryHeight } = state;
  const relief = state.surface.heightField;
  const n = width * height;
  const eta = getScratch(state, 'bodyEta', n);
  const fx = getScratch(state, 'bodyFx', n);
  const fy = getScratch(state, 'bodyFy', n);
  const tiltX = (env.tiltX || 0) * p.tiltCoupling;
  const tiltY = (env.tiltY || 0) * p.tiltCoupling;

  let maxRate = 0;
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const i = y * width + x;
        const v = bodyVol[i];
        if (v > BODY_PRESENT) {
          const rate = bodyMob[i] * bodyOpen[i] * bodyOpen[i] * v * v * v;
          if (rate > maxRate) maxRate = rate;
        }
      }
    }
  }
  if (maxRate <= 1e-7) return 0;
  const stable = Math.min(p.maxSubstep, 0.2 / maxRate);
  const substeps = Math.max(1, Math.min(MAX_BODY_SUBSTEPS, Math.ceil(dt / stable)));
  const h = dt / substeps;

  const flux = (i, j, tilt) => {
    const drive = eta[i] - eta[j] + tilt;
    if (drive === 0) return 0;
    const src = drive > 0 ? i : j;
    const v = bodyVol[src];
    if (v < BODY_PRESENT) return 0;
    const open = bodyOpen[src];
    const excess = Math.abs(drive) - bodyYield[src] / Math.max(v, 0.02);
    if (excess <= 0) return 0;
    const amount = bodyMob[src] * open * open * v * v * v * excess * h;
    return drive > 0 ? amount : -amount;
  };

  for (let s = 0; s < substeps; s += 1) {
    for (const t of region.tiles) {
      const [x0, y0, x1, y1] = tileBounds(state, t);
      for (let y = y0; y < y1; y += 1) {
        for (let x = x0; x < x1; x += 1) {
          const i = y * width + x;
          eta[i] = bodyVol[i] + dryHeight[i] + p.reliefScale * relief[i];
        }
      }
    }
    clearFaces(state, region, fx, fy);
    for (const t of region.tiles) {
      const [x0, y0, x1, y1] = tileBounds(state, t);
      for (let y = y0; y < y1; y += 1) {
        for (let x = x0; x < x1; x += 1) {
          const i = y * width + x;
          if (x + 1 < width) fx[i] = flux(i, i + 1, tiltX);
          if (y + 1 < height) fy[i] = flux(i, i + width, tiltY);
        }
      }
    }
    limitOutflow(state, region, fx, fy, bodyVol);
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      if (state.slots[k]) carryAlongFaces(state, region, fx, fy, bodyVol, state.bodyPig[k]);
    }
    applyFaces(state, region, fx, fy, bodyVol);
  }
  return substeps;
}

// Composites settled watercolour pigment at cell i permanently into the dried stack.
export function lockDepositedPigment(state, i) {
  let any = false;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) if (state.slots[k] && state.depPig[k][i] > 1e-7) any = true;
  if (!any) return;
  compositeAmountsIntoStack(state, i, state.depPig);
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) state.depPig[k][i] = 0;
}

function compositeAmountsIntoStack(state, i, fields) {
  const base = i * BAND_COUNT;
  const ledger = state.ledger || (state.ledger = { evaporated: 0, bakedPigment: 0, scraped: 0 });
  for (let band = 0; band < BAND_COUNT; band += 1) {
    let KX = 0;
    let SX = 0;
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      const pigment = state.slots[k];
      if (!pigment) continue;
      const a = fields[k][i];
      if (a <= 1e-7) continue;
      KX += a * pigment.K[band];
      SX += a * pigment.S[band];
    }
    state.dryRefl[base + band] = kmLayerReflectance(KX, SX, state.dryRefl[base + band]);
  }
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) ledger.bakedPigment += fields[k][i];
}

export function cureBodyAt(state, i) {
  lockDepositedPigment(state, i);
  compositeAmountsIntoStack(state, i, state.bodyPig);
  state.dryHeight[i] += state.bodyVol[i] * state.bodySolids[i];
  state.bodyVol[i] = 0;
  state.bodyOpen[i] = 0;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) state.bodyPig[k][i] = 0;
}

export function stepBodyDrying(state, region, dt, env = {}) {
  const scale = env.dryingScale ?? 1;
  const { width, bodyVol, bodyOpen, bodyDry } = state;
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const i = y * width + x;
        if (bodyVol[i] <= BODY_PRESENT) {
          if (bodyVol[i] > 0) cureBodyAt(state, i);
          continue;
        }
        // Thin films dry faster than thick ones (solvent/oxygen must cross the film).
        const thicknessFactor = 1 / (1 + 0.6 * bodyVol[i]);
        bodyOpen[i] -= dt * bodyDry[i] * scale * thicknessFactor;
        if (bodyOpen[i] <= 0) cureBodyAt(state, i);
      }
    }
  }
}
